import {
    consoleLogger, CoreDI, CoreSettingsParameters, DeviceType,
    inject, Installer, ISettings, LanguageService, lazyInject, PostMessageHandler
} from '@gongxigames/framework/core';
import {
    BetInfo,
    BigWinEvents,
    BottomPanelEvents,
    BuyBonusButtonEvents,
    BuyBonusPopUpEvents,
    FlowModuleEvents,
    GameRulesEvents,
    GenericPopupEvents,
    GenericPopupType,
    InfoPageEvents,
    KeypadControlsEvents,
    LogicEvents,
    MenuBarModule,
    MessagePopup,
    ReelsEvents,
    RoundInfo,
    UIButtons,
    UIButtonsChangeEnableEvent,
    UIDisableReason,
    WinMeterEvents,
    WinPresentationEvents
} from '@gongxigames/framework/slot';
import {slotStore, strictUiEmitter} from '@gongxigames/framework/slotV2/globals';
import {UIMenuService} from '@gongxigames/framework/slotV2/ui/services/UIMenu/UIMenuService';

type IsOpenStatusUpdate = { isOpen: boolean; };
type EnableStatusUpdate = { enabled: boolean; };
type InProgressStatusUpdate = { inProgress: boolean; };
type BlockingReason = 'round' | 'ui' | 'feature';
type SystemPopupData = { shown: boolean; type: string; message: string; };

export type G2WRubyplayClientSideMessages = {
    'rp.g2w.RedirectToLobby': void;
    'rp.g2w.soundStatusUpdate': EnableStatusUpdate;
    'rp.g2w.loadingEnded': void;
    'rp.g2w.gameStarted': void;
    'rp.g2w.gameRulesStatusUpdate': IsOpenStatusUpdate;
    'rp.g2w.payTableStatusUpdate': IsOpenStatusUpdate;
    'rp.g2w.gameHistoryStatusUpdate': IsOpenStatusUpdate;
    'rp.g2w.turboStatusUpdate': EnableStatusUpdate;
    'rp.g2w.fullscreenStatusUpdate': EnableStatusUpdate;
    'rp.g2w.feature': InProgressStatusUpdate;
    'rp.g2w.autoplay': InProgressStatusUpdate;
    'rp.g2w.balanceUpdate': { balance: number; };
    'rp.g2w.systemPopup': SystemPopupData;
    'rp.g2w.paidRoundStarted': void;
    'rp.g2w.paidRoundEnded': RoundInfo;
    'rp.g2w.betUpdate': BetInfo;
    'rp.g2w.gamePause': { pause: boolean; };
};

// eslint-disable-next-line
type W2GRubyplayClientSideMessages = {
    'rp.w2g.refreshBalance': void;
    'rp.w2g.stopAutoPlay': void;
    'rp.w2g.pause': void;
    'rp.w2g.resume': void;
    'rp.w2g.toggleGameRules': void;
    'rp.w2g.toggleGameHistory': void;
    'rp.w2g.toggleInfoPage': void;
    'rp.w2g.mute': void;
    'rp.w2g.unmute': void;
};

type ObservableUIButtons = UIButtons | 'sound';

export type G2WRubyplayClientSideMessagesMap = { [key in keyof G2WRubyplayClientSideMessages]: G2WRubyplayClientSideMessages[key] };

export class ClientSideMessages {
    public static readonly QUIT_TO_LOBBY: symbol = Symbol('QUIT_TO_LOBBY');
}

type ToggleMenuBarEvents = 'UI.menu.infoPageClicked' | 'UI.menu.historyButtonClicked' | 'UI.menu.gameRulesClicked';

export class RubyPlayPostMessageHandler extends PostMessageHandler<G2WRubyplayClientSideMessagesMap> {
    @lazyInject(CoreDI.Settings)
    protected _settings!: ISettings<CoreSettingsParameters>;
    @lazyInject(CoreDI.Installer)
    protected _installer!: Installer;
    @lazyInject(CoreDI.LanguageService)
    private _languageService!: LanguageService;

    protected _balance?: number;
    protected _popupIsShown = false;
    // TODO: _paidRoundStated was created for case in Dragon Prophecy when it sends General.Flow.PAID_ROUND_ENDED 2 times (CLFR-3790)
    // TODO: should be removed when this will be fixed in game related code
    protected _paidRoundStated = false;
    protected _blockingPauseReasons = new Set<BlockingReason>();
    protected _pauseRequested = false;
    protected _canToggleInfoButton = true;
    protected _canToggleGameRulesButton = true;
    protected _canToggleGameHistoryButton = true;
    protected _canToggleSoundButton = true;
    protected _isInfoPageOpen = false;
    protected _isGameRulesOpen = false;
    protected _isGameHistoryOpen = false;
    protected _menuBarOpen = false;
    protected _isInAutoplay = false;

    protected _disabledButtonsByMap: Record<ObservableUIButtons, Array<symbol>> = {
        gameRules: [],
        autoplay: [],
        bet: [],
        turbo: [],
        home: [],
        menu: [],
        totalBetOpenButton: [],
        info: [],
        sound: [],
        gameHistory: []
    };

    public get readyToPause(): boolean {
        return this._pauseRequested && this._blockingPauseReasons.size === 0;
    }

    public get canToggleSoundButton(): boolean {
        return this._canToggleSoundButton || this._menuBarOpen;
    }

    public get canToggleGameRulesButton(): boolean {
        return !this._isInAutoplay && (this._canToggleGameRulesButton || this._menuBarOpen);
    }

    public get canToggleGameHistoryButton(): boolean {
        return !this._isInAutoplay && (this._canToggleGameHistoryButton || this._menuBarOpen);
    }

    public get canToggleInfoButton(): boolean {
        return !this._isInAutoplay && (this._canToggleInfoButton || this._menuBarOpen);
    }

    protected setupEvents(): void {
        this.on(ClientSideMessages.QUIT_TO_LOBBY, this.sendQuitToLobby);

        strictUiEmitter.on('UI.sounds.switchedTo', this.sendSoundStateUpdate.bind(this), {owner: this});
        this.on('General.Game.INITIALIZED', this.sendLoadingEnd);
        this.on('General.Game.STARTED', this.sendGameStarted);

        this.on(BottomPanelEvents.GAME_RULES_CLICKED, this.sendGameRulesStateUpdate.bind(this, true));

        this.on(KeypadControlsEvents.INFO_CLICKED, this.sendPayTableStateUpdate.bind(this, true));

        this.on(BottomPanelEvents.HISTORY_BUTTON_CLICKED, this.sendGameHistoryStateUpdate.bind(this, true));

        this.on(KeypadControlsEvents.TURBO_BUTTON_CLICKED, this.onTurboBtnClicked);
        this.on('UI.Buttons.TurboMode', this.onTurboBtnClicked);
        this._eventManager.on(BottomPanelEvents.FULLSCREEN_CLICKED, this.onFullscreenBtnClicked.bind(this), this);
        strictUiEmitter.on('UI.command.fullScreen', this.onFullscreenBtnClicked.bind(this));

        this.on(FlowModuleEvents.FEATURE_STARTED, this.onFeatureStarted);
        this.on('General.Game.RESTORED', this.onFeatureStarted);
        this.on(FlowModuleEvents.FEATURE_FINISHED, this.onFeatureFinished);

        this.on('Autoplay.STARTED', this.onAutoplayStarted);
        this.on('Autoplay.STOPPED', this.onAutoplayFinished);

        this.on('General.UI.BALANCE.UPDATED', this.onBalanceUpdate);
        this.on(LogicEvents.BALANCE_UPDATED, this.onLogicBalanceUpdated);

        this.on('General.Popups.Add', this.onPopupShown);
        this.on('General.Popups.Remove', this.onPopupRemove);
        this.on('General.ResourcesLoading.ERROR', this.onPopupShown);

        this.on(LogicEvents.ROUND_START, this.onPaidRoundStarted);
        this.on('General.Flow.PAID_ROUND_ENDED', this.onPaidRoundEnded);

        this.on(LogicEvents.BET_CHANGED, this.sendBetUpdate);

        this.on(LogicEvents.ROUND_STARTED, this.addBlockingProcessReason.bind(this, 'round'));
        this.on(FlowModuleEvents.NO_WIN, this.removeBlockingProcessReason.bind(this, 'round'));
        this.on(WinPresentationEvents.INITIAL_ANIMATION_SKIPPED, this.removeBlockingProcessReason.bind(this, 'round'));
        this.on('General.WinPresentation.WIN_METER_FINISHED', this.removeBlockingProcessReason.bind(this, 'round'));
        this.on(WinMeterEvents.SNAP_TO_FINAL_VALUE, this.removeBlockingProcessReason.bind(this, 'round'));
        this.on(WinMeterEvents.STOP_AFTER_SHOWING_FINAL_VALUE, this.removeBlockingProcessReason.bind(this, 'round'));
        this.on(BigWinEvents.FINISHED, this.removeBlockingProcessReason.bind(this, 'round'));

        this.on(BuyBonusButtonEvents.BUY_BONUS_BUTTON_CLICKED, this.addBlockingProcessReason.bind(this, 'feature'));
        this.on(BuyBonusPopUpEvents.BUY_POP_UP_CLOSED, this.removeBlockingProcessReason.bind(this, 'feature'));

        this.on('General.UI.ENABLE', this.enableAllUIButtonsListener);
        this.on('General.UI.DISABLE', this.disableAllUIButtonsListener);
        this.on('General.UI.Button.ENABLE', this.onEnableUIButton);
        this.on('General.UI.Button.DISABLE', this.onDisableUIButton);
        this.on(ReelsEvents.SPIN_ENDED, this.onAllReelsStopped);
        this.on(GenericPopupEvents.INTRO_STARTED, this.onPopupStarted);
        this.on(GenericPopupEvents.POPUP_CLOSED, this.onPopupClosed);
        this.on(FlowModuleEvents.FREESPINS_WIN, this.onFreeSpinsWin);
        this.on(FlowModuleEvents.FREESPINS_RESTORED, this.onFreeSpinsRestored);
        this.on(FlowModuleEvents.RESPIN_ON_FREESPINS_RESTORED, this.onFreeSpinsRestored);
        this.on(ReelsEvents.REELS_SPINNING_STARTED, this.onReelsSpinningStarted);
        this.on('UI.sounds.switchedTo', this.onSoundSwitchedTo);

        // TODO use only one event (new 'UI.infoPage.opened')

        strictUiEmitter.on('UI.menu.opened', this.onMenuBarOpened.bind(this));
        strictUiEmitter.on('UI.menu.closed', this.onMenuBarClosed.bind(this));
        strictUiEmitter.on('UI.menu.infoPageClicked', this.onMenuBarInfoPageClicked.bind(this));
        this.on(InfoPageEvents.OPENED, this.onInfoPageOpened);
        this.on(InfoPageEvents.CLOSED, this.onInfoPageClosed);
        strictUiEmitter.on('UI.infoPage.opened', this.onInfoPageOpened.bind(this));
        strictUiEmitter.on('UI.infoPage.closed', this.onInfoPageClosed.bind(this));

        strictUiEmitter.on('UI.menu.gameRulesClicked', this.onMenuBarGameRulesClicked.bind(this));
        strictUiEmitter.on('UI.gameRules.opened', this.onGameRulesOpened.bind(this));
        strictUiEmitter.on('UI.gameRules.closed', this.onGameRulesClosed.bind(this));
        this.on(GameRulesEvents.OPENED, this.onGameRulesOpened);
        this.on(GameRulesEvents.CLOSED, this.onGameRulesClosed);

        strictUiEmitter.on('UI.menu.historyButtonClicked', this.onMenuBarHistoryButtonClicked.bind(this));
        this.on('General.GameHistory.Opened', this.onGameHistoryOpened);
        this.on('General.GameHistory.Closed', this.onGameHistoryClosed);

        strictUiEmitter.on('UI.gameHistory.opened', this.onGameHistoryOpened.bind(this));
        strictUiEmitter.on('UI.gameHistory.closed', this.onGameHistoryClosed.bind(this));
    }

    protected isPlatformMobile(): boolean {
        return this._settings.get('platform') === DeviceType.MOBILE;
    }

    protected setupListeners(): void {
        this._browser.addEventListener('message', this.onReceivedMessageCallback.bind(this), false);
    }

    protected onReceivedMessageCallback(event: MessageEvent): void {
        const data = this.getPostMessagePayload(event);

        if (!data) {
            return;
        }

        switch (data.type) {
            case 'rp.w2g.refreshBalance': {
                this.onRefreshBalance();
                break;
            }
            case 'rp.w2g.stopAutoPlay': {
                this.onStopAutoPlay();
                break;
            }
            case 'rp.w2g.pause': {
                this.onPause(true);
                break;
            }
            case 'rp.w2g.resume': {
                this.onPause(false);
                break;
            }
            case 'rp.w2g.mute': {
                this.muteSounds();
                break;
            }
            case 'rp.w2g.unmute': {
                this.unmuteSounds();
                break;
            }
            case 'rp.w2g.toggleInfoPage': {
                this.toggleInfoPage();
                break;
            }
            case 'rp.w2g.toggleGameRules': {
                this.toggleGameRules();
                break;
            }
            case 'rp.w2g.toggleGameHistory': {
                this.toggleGameHistory();
                break;
            }
            default: {
                consoleLogger.warn('Message is not recognized', data);
                break;
            }
        }
    }

    protected getPostMessagePayload(event: MessageEvent): Record<string, any> | undefined {
        let data = event.data;

        if (typeof data === 'string') {
            try {
                data = JSON.parse(data);
            } catch (error) {
                return consoleLogger.error(error);
            }
        }

        const isNotAnObject = typeof data !== 'object';
        const isFromTheSamePage = window.location.origin === event.origin;

        if (isFromTheSamePage) {
            return;
        }

        if (isNotAnObject) {
            consoleLogger.warn('Message is not recognized', data);

            return;
        }

        return data;
    }

    protected disableGameUI(): void {
        this._eventManager.emit('General.UI.DISABLE', PostMessageHandler.NAME);
        this._eventManager.emit('Autoplay.StopButton.DISABLE', PostMessageHandler.NAME);
    }

    protected enableGameUI(): void {
        this._eventManager.emit('General.UI.ENABLE', PostMessageHandler.NAME);
        this._eventManager.emit('Autoplay.StopButton.ENABLE', PostMessageHandler.NAME);
    }

    protected addBlockingProcessReason(reason: BlockingReason): void {
        this._blockingPauseReasons.add(reason);
    }

    protected removeBlockingProcessReason(reason: BlockingReason): void {
        this._blockingPauseReasons.delete(reason);

        this.pauseGame();
    }

    protected pauseGame(): void {
        if (!this.readyToPause){
            return;
        }
        this.disableGameUI();
        this._eventManager.emitAsync('General.System.PAUSE');
        this.sendPauseNotification(true);
    }

    protected unpauseGame(): void {
        this.enableGameUI();
        this._eventManager.emitAsync('General.System.RESUME');
        this.sendPauseNotification(false);
    }

    protected onRefreshBalance(): void {
        this._eventManager.emit('General.Balance.REFRESH');
    }

    protected onStopAutoPlay(): void {
        this._eventManager.emit('Autoplay.FORCE_STOP');
    }

    protected onPause(pause: boolean): void {
        this._pauseRequested = pause;

        if (pause) {
            this.pauseGame();
        } else {
            this.unpauseGame();
        }
    }

    protected toggleGameRules(): void {
        this.toggleMenuBar(this._isGameRulesOpen, this.canToggleGameRulesButton, 'UI.menu.gameRulesClicked');
    }

    protected toggleGameHistory(): void {
        this.toggleMenuBar(this._isGameHistoryOpen, this.canToggleGameHistoryButton, 'UI.menu.historyButtonClicked');
    }

    protected toggleInfoPage(): void {
        this.toggleMenuBar(this._isInfoPageOpen, this.canToggleInfoButton, 'UI.menu.infoPageClicked');
    }

    protected muteSounds(): void {
        strictUiEmitter.emit('UI.sounds.disable');
    }

    protected unmuteSounds(): void {
        strictUiEmitter.emit('UI.sounds.enable');
    }

    protected sendQuitToLobby(): void {
        this.postMessage('rp.g2w.RedirectToLobby');
    }

    protected sendSoundStateUpdate(enabled: boolean): void {
        this.postMessage('rp.g2w.soundStatusUpdate', {enabled});
    }

    protected sendLoadingEnd(): void {
        this.postMessage('rp.g2w.loadingEnded');
    }

    protected sendGameStarted(): void {
        this.postMessage('rp.g2w.gameStarted');
    }

    protected sendGameRulesStateUpdate(isOpen: boolean): void {
        this.postMessage('rp.g2w.gameRulesStatusUpdate', {isOpen});
    }

    protected sendPayTableStateUpdate(isOpen: boolean): void {
        this.postMessage('rp.g2w.payTableStatusUpdate', {isOpen});
    }

    protected sendGameHistoryStateUpdate(isOpen: boolean): void {
        this.postMessage('rp.g2w.gameHistoryStatusUpdate', {isOpen});
    }

    protected sendPauseNotification(pause: boolean): void {
        this.postMessage('rp.g2w.gamePause', {pause});
    }

    protected onTurboBtnClicked(id: string | symbol, enabled: boolean): void {
        this.postMessage('rp.g2w.turboStatusUpdate', {enabled});
    }

    protected onFullscreenBtnClicked(enabled: boolean): void {
        this.postMessage('rp.g2w.fullscreenStatusUpdate', {enabled});
    }

    protected onFeatureStarted(): void {
        this._paidRoundStated = true;

        this.postMessage('rp.g2w.feature', {inProgress: true});

        this.addBlockingProcessReason('feature');
    }

    protected onFeatureFinished(): void {
        this.postMessage('rp.g2w.feature', {inProgress: false});

        this._blockingPauseReasons.clear();

        this.pauseGame();
    }

    protected onAutoplayStarted(): void {
        this._isInAutoplay = true;
        this.postMessage('rp.g2w.autoplay', {inProgress: true});
    }

    protected onAutoplayFinished(): void {
        this._isInAutoplay = false;
        this.postMessage('rp.g2w.autoplay', {inProgress: false});
    }

    protected onBalanceUpdate(id: string | symbol, balance: number): void {
        this._balance ??= balance;

        if (this._balance !== balance) {
            this._balance = balance;

            this.postMessage('rp.g2w.balanceUpdate', {balance: this._balance});
        }
    }

    protected onLogicBalanceUpdated(id: string | symbol, balance: number): void {
        this._balance = balance;

        this.postMessage('rp.g2w.balanceUpdate', {balance: this._balance});
    }

    protected onPopupShown(id: string, popup: MessagePopup): void {
        if (!this._popupIsShown) {
            this._popupIsShown = true;
            this.postMessage('rp.g2w.systemPopup', this.createPopupMessageData(true, popup));
        }
    }

    protected onPopupRemove(id: string, popup: MessagePopup): void {
        if (this._popupIsShown) {
            this._popupIsShown = false;
            this.postMessage('rp.g2w.systemPopup', this.createPopupMessageData(false, popup));
        }
    }

    protected createPopupMessageData(shown: boolean, data: MessagePopup): SystemPopupData {
        let translatedMessage = '';

        if (data.data) {
            translatedMessage =
                typeof data.data.message === 'object' && data.data.message && 'id' in data.data.message
                    ? this._languageService.get(data.data.message.id)
                    : typeof data.data.message === 'string'
                        ? data.data.message
                        : '';
        }

        return {shown, type: data.id, message: translatedMessage};
    }

    protected onPaidRoundStarted(id: string | symbol, bet: number): void {
        if (bet) {
            this._paidRoundStated = true;
            this.postMessage('rp.g2w.paidRoundStarted');
        }
    }

    protected onPaidRoundEnded(id: string | symbol, data: RoundInfo): void {
        if (this._paidRoundStated) {
            this._paidRoundStated = false;
            this.postMessage('rp.g2w.paidRoundEnded', {...data});
        }
    }

    protected sendBetUpdate(id: string | symbol, data: BetInfo): void {
        this.postMessage('rp.g2w.betUpdate', {...data});
    }

    protected enableAllUIButtonsListener(eventID: symbol | string, reason: symbol): void {
        this.enableAllUIButtons(reason);
    }

    protected disableAllUIButtonsListener(eventID: symbol | string, reason: symbol): void {
        this.disableAllUIButtons(reason);
    }

    protected onEnableUIButton(eventID: string, event: UIButtonsChangeEnableEvent): void {
        this.enableUIButtonByName(event.name as ObservableUIButtons, event.reason);
    }

    protected onDisableUIButton(eventID: string, event: UIButtonsChangeEnableEvent): void {
        this.disableUIButtonByName(event.name as ObservableUIButtons, event.reason);
    }

    public enableAllUIButtons(reason: symbol): void {
        Object.keys(this._disabledButtonsByMap).forEach(key => {
            this.enableUIButtonByName(key as ObservableUIButtons, reason);
        });
    }

    public disableAllUIButtons(reason: symbol): void {
        Object.keys(this._disabledButtonsByMap).forEach(key => {
            this.disableUIButtonByName(key as ObservableUIButtons, reason);
        });
    }

    public enableUIButtonByName(buttonName: ObservableUIButtons, reason: symbol): void {
        const disabledBy = this._disabledButtonsByMap[buttonName];
        const index = disabledBy?.indexOf(reason);

        if (index == null || index === -1) {
            return;
        }

        disabledBy.splice(index, 1);

        if (!disabledBy.length) {
            this.enableUIButton(buttonName);
        }
    }

    public disableUIButtonByName(buttonName: ObservableUIButtons, reason: symbol): void {
        const disabledBy = this._disabledButtonsByMap[buttonName];
        const index = disabledBy?.indexOf(reason);

        if (index == null || index !== -1) {
            return;
        }

        disabledBy.push(reason);

        this.disableUIButton(buttonName);
    }

    protected enableUIButton(name: ObservableUIButtons): void {
        switch (name) {
            case UIButtons.GAME_RULES: {
                this._canToggleGameRulesButton = true;
                break;
            }
            case UIButtons.INFO: {
                this._canToggleInfoButton = true;
                break;
            }
            case UIButtons.GAME_HISTORY: {
                this._canToggleGameHistoryButton = true;
                break;
            }
            case 'sound': {
                this._canToggleSoundButton = true;
                break;
            }
            default: {
                consoleLogger.warn(`There's no action for button "${name}"`);
            }
        }
    }

    protected disableUIButton(name: ObservableUIButtons): void {
        switch (name) {
            case UIButtons.GAME_RULES: {
                this._canToggleGameRulesButton = false;
                break;
            }
            case UIButtons.INFO: {
                this._canToggleInfoButton = false;
                break;
            }
            case UIButtons.GAME_HISTORY: {
                this._canToggleGameHistoryButton = false;
                break;
            }
            case 'sound': {
                this._canToggleSoundButton = false;
                break;
            }
            default: {
                consoleLogger.warn(`There's no action for button "${name}"`);
            }
        }
    }

    protected onFreeSpinsIntroStarted(): void {
        this.enableAllUIButtons(UIDisableReason.TRIGGERING_FEATURE);
        this.disableAllUIButtons(UIDisableReason.FREE_SPIN_MODE);
    }

    protected onFreeSpinsOutroFinished(): void {
        this.enableAllUIButtons(UIDisableReason.TRIGGERING_FEATURE);
        this.enableAllUIButtons(UIDisableReason.FREE_SPIN_MODE);
    }

    protected onBonusOutroFinished(): void {
        this.enableAllUIButtons(UIDisableReason.TRIGGERING_FEATURE);
    }

    protected onAllReelsStopped(): void {
        this.enableAllUIButtons(UIDisableReason.SPINNING);
    }

    protected onPopupStarted(id: any, type: symbol): void {
        if (type === GenericPopupType.FREESPIN_INTRO) {
            this.onFreeSpinsIntroStarted();
        }
    }

    protected onPopupClosed(id: any, type: symbol): void {
        if (type === GenericPopupType.FREESPIN_OUTRO) {
            this.onFreeSpinsOutroFinished();
        }
    }

    protected onFreeSpinsWin(): void {
        this.disableAllUIButtons(UIDisableReason.FREE_SPIN_MODE);
    }

    protected onFreeSpinsRestored(id: any): void {
        this.disableAllUIButtons(UIDisableReason.FREE_SPIN_MODE);
    }

    protected onReelsSpinningStarted(): void {
        this.disableAllUIButtons(UIDisableReason.SPINNING);
    }

    protected onMenuBarOpened(): void {
        this._menuBarOpen = true;
    }

    protected onMenuBarClosed(): void {
        this._menuBarOpen = false;
    }

    protected onMenuBarInfoPageClicked(): void {
        this.sendPayTableStateUpdate(true);

        this._menuBarOpen = this.isPlatformMobile();
    }

    protected onInfoPageOpened(): void {
        this._isInfoPageOpen = true;
    }

    protected onInfoPageClosed(): void {
        this.sendPayTableStateUpdate(false);

        this._isInfoPageOpen = false;
    }

    protected onMenuBarGameRulesClicked(): void {
        this.sendGameRulesStateUpdate(true);

        this._menuBarOpen = this.isPlatformMobile();
    }

    protected onGameRulesOpened(): void {
        this._isGameRulesOpen = true;
    }

    protected onGameRulesClosed(): void {
        this.sendGameRulesStateUpdate(false);

        this._isGameRulesOpen = false;
    }

    protected onMenuBarHistoryButtonClicked(): void {
        this.sendGameHistoryStateUpdate(true);

        this._menuBarOpen = this.isPlatformMobile();
    }

    protected onGameHistoryOpened(): void {
        this._isGameHistoryOpen = true;
    }

    protected onGameHistoryClosed(): void {
        this.sendGameHistoryStateUpdate(false);

        this._isGameHistoryOpen = false;
    }

    protected onSoundSwitchedTo(id: string | symbol, isOn: boolean): void {
        this.sendSoundStateUpdate(isOn);
    }

    protected toggleMenuBar(isOpen: boolean, canToggleButton: boolean, event: ToggleMenuBarEvents): void {
        if (isOpen) {
            this.closeMenuBar();

            return;
        }

        if (!canToggleButton) {
            return;
        }

        this.openMenuBar(event);
    }

    protected openMenuBar(event: ToggleMenuBarEvents): void {
        let methodName: 'openPaytable' | 'openGameRules' | 'openGameHistory';

        switch (event) {
            case 'UI.menu.infoPageClicked': {
                methodName = 'openPaytable';
                break;
            }
            case 'UI.menu.gameRulesClicked': {
                methodName = 'openGameRules';
                break;
            }
            case 'UI.menu.historyButtonClicked': {
                methodName = 'openGameHistory';
                break;
            }
            default: {
                throw new Error(`Unknown event: ${event}`);
            }
        }

        if (slotStore.slot_isGameStateMachineActive.value) {
            const menuBarService = this._installer.getComponent<UIMenuService>('UIMenuService');

            if (!this._menuBarOpen) {
                menuBarService.openMenuBar();
            }
            menuBarService[methodName]();
        } else {
            if (this.isPlatformMobile()) {
                const menuBarModule = this._installer.getComponent<MenuBarModule>(MenuBarModule.NAME);

                if (!this._menuBarOpen) {
                    menuBarModule.openMenuBar();
                }
                menuBarModule[methodName]();
            } else {
                strictUiEmitter.emit(event);
            }
        }
    }

    protected closeMenuBar(): void {
        if (slotStore.slot_isGameStateMachineActive.value) {
            const menuBarService = this._installer.getComponent<UIMenuService>('UIMenuService');

            menuBarService.closeMenuBar();
        } else {
            if (this.isPlatformMobile()) {
                const menuBarModule = this._installer.getComponent<MenuBarModule>(MenuBarModule.NAME);

                menuBarModule.closeMenuBar();
            } else {
                strictUiEmitter.emit('UI.menu.closed');
            }
        }
    }
}
